import { ActionButton, EditButton } from 'components/button/button.styled'
import { LoadingContainer } from 'components/loadingContainer'
import { useImageById } from 'hooks/user/useImage/useImageById'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { EventDetails, Ticket } from 'types/Event'
import { SeatStatus } from 'types/Location'
import { calculateIsSoldOut, formatDateYear, formatTime } from 'utils/event'

import {
    ActionContainer,
    ButtonContainer,
    Container,
    DateDisplay,
    DetailsContainer,
    ImageComponent,
    ImageContainer,
    LocationBox,
    ProgressBar,
    ProgressFill,
    TicketDataContainer,
    TicketDataLabel,
    TicketDataLarge,
    TicketDataRow,
    TicketDataValue,
    TicketDataValueLarge,
    Title,
    WrapperColumn,
    WrapperRow,
} from './eventCard.style'

interface Props {
    data: EventDetails
    onEdit: (data: any) => void
}

export const EventCard = ({ data, onEdit }: Props) => {
    const imageSource = data?.custom_image ?? data?.show?.cover_image
    const { data: imageData, isLoading: isImageLoading, error: ImageError } = useImageById(imageSource)
    const navigate = useNavigate()

    const formattedDate = formatDateYear(new Date(data.event_date))
    const formattedTime = formatTime(new Date(data.event_date))

    const handleLocalSelling = useCallback(() => {
        navigate(`/admin/jegyek/eladas/${data.event_id}`)
    }, [data.event_id, navigate])

    const handleRedirect = useCallback(() => {
        navigate(`/musor/${data.show_id}`)
    }, [data.show_id, navigate])

    const { sold, total, percentage } = useMemo(() => calculateTicketStats(data), [data])
    const totalRevenue = useMemo(() => calculateTotalRevenue(data.tickets), [data.tickets])

    const isSoldOut = data && data.location ? calculateIsSoldOut(data.location, data.tickets ?? []) : false
    const sellingDateIsOverDue = data && data.selling_end_date ? new Date(data.selling_end_date) < new Date() : false
    const saleIsOver = isSoldOut || sellingDateIsOverDue

    return (
        <LoadingContainer isLoading={isImageLoading} error={ImageError}>
            <Container saleisover={saleIsOver}>
                <ImageContainer>
                    <ImageComponent alt="show_image" src={imageData?.image_url} />
                </ImageContainer>
                <WrapperColumn>
                    <WrapperRow>
                        <DetailsContainer>
                            <Title onClick={handleRedirect}>{data.show?.name || 'N/A'}</Title>
                            <LocationBox style={{ backgroundColor: data.location?.color || '#ba9d31' }}>
                                {data.location?.name || 'N/A'}
                            </LocationBox>
                            <TicketDataRow>
                                <TicketDataLabel>{`Bruttó jegyár:`}</TicketDataLabel>
                                <TicketDataValue>{getPriceRange(data.tickets ?? [])}</TicketDataValue>
                            </TicketDataRow>
                        </DetailsContainer>
                        <ActionContainer>
                            <ButtonContainer>
                                <EditButton onClick={() => onEdit(data)}>Esemény szerkesztése</EditButton>
                                {saleIsOver ? (
                                    <ActionButton disabled>Az értékesítés lezárult</ActionButton>
                                ) : (
                                    <ActionButton onClick={handleLocalSelling}>Kasszás jegyértékesítés</ActionButton>
                                )}
                            </ButtonContainer>
                            <DateDisplay>{formattedDate}</DateDisplay>
                            <DateDisplay>{formattedTime}</DateDisplay>
                        </ActionContainer>
                    </WrapperRow>
                    <TicketDataContainer>
                        {total === 0 ? (
                            <TicketDataLabel>Még nincsenek jegyek rendelve az eseményhez</TicketDataLabel>
                        ) : (
                            <>
                                <TicketDataRow>
                                    <TicketDataLarge>{`Bruttó bevétel:`}</TicketDataLarge>
                                    <TicketDataValueLarge>{`${totalRevenue}`}</TicketDataValueLarge>
                                </TicketDataRow>
                                <TicketDataRow>
                                    <TicketDataLabel>{`Jegyeladás:`}</TicketDataLabel>
                                    <TicketDataValue>{`${sold}/${total}`}</TicketDataValue>
                                </TicketDataRow>
                            </>
                        )}
                    </TicketDataContainer>
                    <ProgressBar>
                        <ProgressFill color={data.location?.color || '#000000'} percentage={`${percentage}%`} />
                    </ProgressBar>
                </WrapperColumn>
            </Container>
        </LoadingContainer>
    )
}

/**
 * Returns the ticket price range as a string with "Ft (bruttó)"
 * @param tickets array of tickets for an event
 * @returns string like "1000 Ft (bruttó)" or "1000 - 3000 Ft (bruttó)"
 */
export function getPriceRange(tickets: Ticket[]): string {
    if (!tickets || tickets.length === 0) return 'N/A'

    const prices = tickets.map((t) => Number(t.price ?? 0))
    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)

    const formatter = new Intl.NumberFormat('hu-HU')

    return minPrice === maxPrice ? `${formatter.format(minPrice)} Ft` : `${formatter.format(minPrice)} - ${formatter.format(maxPrice)} Ft`
}
/**
 * Calculates ticket statistics including sold tickets, total tickets, and percentage sold
 * @param data object containing number_of_sold_tickets, number_of_arrived_tickets, and number_of_all_tickets
 * @returns object with sold, total, and percentage properties
 */
export const calculateTicketStats = (data: {
    number_of_sold_tickets?: number
    number_of_arrived_tickets?: number
    number_of_all_tickets?: number
}) => {
    const soldAndArrived = (data.number_of_sold_tickets ?? 0) + (data.number_of_arrived_tickets ?? 0)
    const total = data.number_of_all_tickets ?? 0
    const percentage = total > 0 ? Math.round((soldAndArrived / total) * 100) : 0
    return { sold: soldAndArrived, total, percentage }
}

/**
 * Calculates the total revenue from sold tickets
 * @param tickets
 * @returns
 */
export const calculateTotalRevenue = (tickets: Ticket[] | undefined): string => {
    if (!tickets || tickets.length === 0) return '0 Ft'

    const total = tickets.reduce((sum, ticket) => {
        const { status, price, is_complimentary } = ticket
        if ((status === SeatStatus.Arrived || status === SeatStatus.Sold) && !is_complimentary) {
            return sum + Number(price ?? 0)
        }
        return sum
    }, 0)

    return new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: 'HUF',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(total)
}
